import { useEffect, useRef, useState } from "react";

import { useAuthStore } from "@/lib/auth-store";
import {
  haalPortaalVolume,
  isPortaalGeluidAan,
} from "@/lib/apparaatInstellingen";
import {
  meldGebruikersinteractie,
  speelToon,
  type ToonSoort,
} from "@/lib/meldingsgeluid";
import { useIsLive } from "@/lib/hooks/usePublishingStatus";
import { useChatTellers } from "@/lib/hooks/useChatTellers";
import { useWerkvoorraadTellers } from "@/lib/hooks/useWerkvoorraadTellers";

/**
 * Meldingen in het portaal zelf (08-09): een korte toon en een teller
 * in de tabtitel bij een nieuwe chatsessie, een nieuw bericht in een
 * chat die ik heb overgenomen, een nieuwe WhatsApp en een nieuwe mail
 * die actie vraagt.
 *
 * Geen eigen polling en geen eigen stream: de hook hangt aan de
 * bestaande teller-query's van de zijbalk (dezelfde query-keys, dus
 * react-query bedient beide met één verzoek — het 60s-ritme én de
 * invalidatie na elke mutatie gelden hier vanzelf mee). Een melding is
 * een *stijging* van een van die tellers; de eerste meting na het
 * laden is alleen de nulstand en geeft dus nooit geluid.
 *
 * De teller in de titel telt op tot het tabblad weer actief wordt.
 * Geluid staat standaard aan en is per apparaat uit te zetten op
 * Profiel (naast de pushkaart) — het is een browserinstelling, geen
 * accountinstelling.
 *
 * Sessie 4 (09-09): de toon komt niet meer uit `/melding.wav` (een
 * vage piep, één klank voor alles) maar uit `@/lib/meldingsgeluid`,
 * dat hem opwekt en per soort verschilt. De hook luistert daarnaast
 * naar de service worker: die stuurt bij elke binnenkomende push de
 * soort mee, zodat een overdracht en een wachtende klant hoorbaar
 * anders klinken dan een gewone nieuwe mail — die twee hebben namelijk
 * geen teller die stijgt.
 */

const BASIS_TITEL = "VoerOnline Klantenservice";

/**
 * Twee bronnen (tellerstijging en pushbericht) kunnen dezelfde
 * gebeurtenis melden. Binnen dit venster klinkt er hoogstens één toon.
 */
const ONTDUBBEL_MS = 2000;

/** Welke toon hoort bij welke pushsoort? */
const TOON_PER_SOORT: Record<string, ToonSoort> = {
  chat_overdracht: "overdracht",
  klant_wacht: "klant_wacht",
};

/** De tellers die een melding waard zijn, in de volgorde van de zijbalk. */
type Meetpunt = "chat" | "whatsapp" | "email" | "aan_mij";
const MEETPUNTEN: readonly Meetpunt[] = [
  "chat",
  "whatsapp",
  "email",
  "aan_mij",
];

let laatsteToonOp = 0;

/**
 * Speel de meldingstoon van `soort`. Stil wanneer geluid uitstaat, het
 * volume op nul staat, de gebruiker nog nergens geklikt heeft
 * (browserregel) of er net al een toon klonk. In al die gevallen
 * blijft de teller in de tabtitel het signaal.
 */
export function speelMeldingsgeluid(soort: ToonSoort = "nieuw"): void {
  if (!isPortaalGeluidAan()) return;
  const nu = Date.now();
  if (nu - laatsteToonOp < ONTDUBBEL_MS) return;
  if (speelToon(soort, haalPortaalVolume() / 100)) {
    laatsteToonOp = nu;
  }
}

/** Alleen voor tests: de ontdubbeling terugzetten op nul. */
export function _resetOntdubbeling(): void {
  laatsteToonOp = 0;
}

export function usePortaalMeldingen(): void {
  const isLive = useIsLive();
  const gebruiker = useAuthStore((s) => s.gebruiker);
  const ingelogd = Boolean(useAuthStore((s) => s.accessToken));
  const aan = isLive && ingelogd;

  const { data: wvTellers } = useWerkvoorraadTellers({ enabled: aan });
  const { data: chatTellers } = useChatTellers({
    enabled: aan && gebruiker?.rol === "admin",
  });

  const [ongelezen, setOngelezen] = useState(0);
  // De vorige stand per meetpunt; null zolang er nog niets gemeten is.
  const vorigeRef = useRef<Record<Meetpunt, number> | null>(null);

  useEffect(() => {
    if (!wvTellers) return;
    const nu: Record<Meetpunt, number> = {
      // De chat-teller van het admin-endpoint is preciezer (per
      // kanaal, ongelezen activiteit); is die er niet, dan volstaat de
      // werkvoorraad-teller.
      chat: chatTellers?.chat ?? wvTellers.chat.nieuw,
      whatsapp: chatTellers?.whatsapp ?? wvTellers.whatsapp.nieuw,
      email: wvTellers.email.nieuw,
      aan_mij: wvTellers.aan_mij.nieuw,
    };

    const vorige = vorigeRef.current;
    vorigeRef.current = nu;
    if (!vorige) return; // eerste meting: alleen de nulstand vastleggen

    const erbij = MEETPUNTEN.reduce(
      (som, punt) => som + Math.max(0, nu[punt] - vorige[punt]),
      0,
    );
    if (erbij === 0) return;

    speelMeldingsgeluid();
    if (document.visibilityState !== "visible") {
      setOngelezen((n) => n + erbij);
    }
  }, [wvTellers, chatTellers]);

  // De browser laat geluid pas toe ná een gebruikersinteractie. Eén
  // keer luisteren volstaat: daarna staat de AudioContext open.
  useEffect(() => {
    function bijInteractie() {
      meldGebruikersinteractie();
    }
    const opties = { once: true } as const;
    window.addEventListener("pointerdown", bijInteractie, opties);
    window.addEventListener("keydown", bijInteractie, opties);
    return () => {
      window.removeEventListener("pointerdown", bijInteractie);
      window.removeEventListener("keydown", bijInteractie);
    };
  }, []);

  // Pushmeldingen die geen teller laten stijgen (overdracht, wachtende
  // klant) melden zichzelf via de service worker.
  useEffect(() => {
    if (!aan || !("serviceWorker" in navigator)) return;
    function bijBericht(gebeurtenis: MessageEvent) {
      const data = gebeurtenis.data as
        | { type?: string; soort?: string }
        | undefined;
      if (data?.type !== "push-melding") return;
      const toon = TOON_PER_SOORT[data.soort ?? ""];
      if (!toon) return; // gewone soorten: de teller doet het al
      speelMeldingsgeluid(toon);
      if (document.visibilityState !== "visible") {
        setOngelezen((n) => n + 1);
      }
    }
    navigator.serviceWorker.addEventListener("message", bijBericht);
    return () => {
      navigator.serviceWorker.removeEventListener("message", bijBericht);
    };
  }, [aan]);

  // Zodra het tabblad actief wordt, is de teller gezien.
  useEffect(() => {
    function bijZichtbaar() {
      if (document.visibilityState === "visible") setOngelezen(0);
    }
    document.addEventListener("visibilitychange", bijZichtbaar);
    window.addEventListener("focus", bijZichtbaar);
    return () => {
      document.removeEventListener("visibilitychange", bijZichtbaar);
      window.removeEventListener("focus", bijZichtbaar);
    };
  }, []);

  useEffect(() => {
    document.title =
      ongelezen > 0 ? `(${ongelezen}) ${BASIS_TITEL}` : BASIS_TITEL;
  }, [ongelezen]);
}
